import Util from 'util/util'

export const GAP_TYPE = {
    OPEN: 0,
    CHOICE: 1,
    DRAG: 2,
    NUMBER: 3,
}

export const CHOICE_GAP_SORTING = {
    ALPHABETICAL: 0,
    ALPHABETICAL_REVERSE: 1,
    SHUFFLE: 2,
}

export function getFragmentsFromTaskInfo(taskModel, sortGapAnswers = false) {
    return taskModel.get('task_info_json').text.reduce((fragments, fragment) => {
        if (fragment instanceof Object) {
            // Ensure the correct answer option of choice and drag gaps is sorted in front of the distractor options.
            if (sortGapAnswers && (fragment.type === GAP_TYPE.CHOICE || fragment.type === GAP_TYPE.DRAG)) {
                fragment.answers.sort((a, b) => Number(b.correct) - Number(a.correct))
            }
            fragments.push(fragment)
            return fragments
        }
        // If string contains a <br> tag, replace this with a proper line break.
        // Then split string by linebreak.
        fragment = fragment.replace(/<br ?\/?>/gi, '\n')
        fragments.push(...fragment.split(/(\n)/).filter(Boolean))
        return fragments
    }, [])
}

// Create text representation of the saved fragments.
// Gaps of possible answers are represented between square brackets.
export function createText(fragments) {
    return Util.unescape(fragments.reduce((text, fragment) => {
        // If fragment is a gap
        if (fragment instanceof Object) {
            const correctAnswers = []
            const distractorAnswers = []
            for (const answer of fragment.answers) {
                if (answer.correct) {
                    correctAnswers.push(answer.text)
                } else {
                    distractorAnswers.push(answer.text)
                }
            }

            // Gap requires at least 1 correct answer.
            if (correctAnswers.length) {

                // Opening bracket of gap.
                text += '[ '

                // Append all correct options seperated by a backslash with spaces around
                // it for improved legiblity.
                text += correctAnswers.join(' \\ ')

                // If there are any distractor options, append a pipe to separate them from
                // the correct answers. Then append the distractor options seperated by a
                // backslash with spaces around it for improved legiblity.
                if (distractorAnswers.length) {
                    text += ' | '
                    text += distractorAnswers.join(' \\ ')
                }

                // Closing bracket of gap.
                text += ' ] '
            }

            return text
        }

        // Append linebreak
        if (fragment === '\n') {
            return text + '\n'
        }

        // If fragment is just plain text, append it with a space behind it.
        return text + fragment + ' '
    }, ''))
}

// Update fragments data based on text input.
export function updateFragmentsFromText(text, fragments) {
    // Collect existing gaps from data to preserve data that cannot be encoded in the text input,
    // including gap type and gap settings.
    const existingGaps = new Map()
    for (const fragment of fragments) {
        if (fragment instanceof Object) {
            existingGaps.set(fragment.id, fragment)
        }
    }

    // Replace any HTML whitespace entities with regular whitespace.
    // Replace all points with more than 2 subsequent spaces with
    // a single space. Replace all groups of more than 2 line
    // breaks and replace them with 2 linebreaks.
    text = Util.normalizeCharacters(text)
    text = text.replace(/\n{3,}/g, '\n\n')
    text = text.replace(/ {2,}/g, ' ')

    // Clear out existing fragments.
    const newFragments = []

    let gapCount = 0

    // Split sentence by bracket groups. For example, the string
    // "Pa's wijze [ lynx ] bezag vroom"
    // becomes
    // ["Pa's wijze", "[ lynx ]", "bezag vroom"]
    for (let textFragment of text.split(/(\[.*?\])/)) {

        // If fragment is starts with an opening square bracket
        // and ends with a closing square bracket, it should be
        // a series of possible answers.
        if (/^\[.+\]$/.test(textFragment)) {
            gapCount++

            const existingGap = existingGaps.get(gapCount)
            const previousGap = findPreviousGap(newFragments, newFragments.length - 1)

            const gapType = existingGap?.type ?? previousGap?.type ?? GAP_TYPE.OPEN

            if (gapType === GAP_TYPE.OPEN) {
                textFragment = Util.stripTags(textFragment)
            } else {
                textFragment = cleanText(textFragment)
            }

            // Extract list of correct and distractor answers from the text inside
            // of the square bracket group.
            let [ correctAnswers = [], distractorAnswers = [] ] = splitAndTrimString(
                // Remove square brackets
                textFragment.replace(/^\[|\]$/g, ''),
                '|'
            )

            // If gap is empty, the gap is of the open type or the gap didn't yet exist,
            // allow an empty answer, which means that this gap cannot be automatically graded.
            if (correctAnswers.length === 0 && gapType === GAP_TYPE.OPEN) {
                correctAnswers = ['']
            } else if (correctAnswers.length) {
                correctAnswers = splitAndTrimString(correctAnswers, /\\/)
            }

            if (correctAnswers.length) {
                const gap = existingGap ?? {
                    id: gapCount,
                    type: gapType,
                    ...pickGapOptions(previousGap),
                }
                if (gapType === GAP_TYPE.NUMBER && existingGap) {
                    // If the gap already existed, copy all answer options except the number text itself.
                    gap.answers = [pickGapAnswerOptions(
                        existingGap,
                        {
                            ...existingGap.answers[0],
                            text: Util.stripTags(correctAnswers[0]),
                            correct: true,
                        }
                    )]
                } else {
                    gap.answers = correctAnswers.map((correctAnswer) => {
                        return {
                            text: gap.type === GAP_TYPE.OPEN ? Util.stripTags(correctAnswer) : cleanText(correctAnswer),
                            correct: true,
                        }
                    })

                    if (distractorAnswers.length) {
                        distractorAnswers = splitAndTrimString(distractorAnswers, /\\/)
                        gap.answers.push(...distractorAnswers.map((distractorAnswer) => {
                            return {
                                text: cleanText(distractorAnswer),
                                correct: false,
                            }
                        }))
                    }
                }

                newFragments.push(gap)

            }
            continue
        }

        // If fragment is not a square bracket group, first split the text by enclosed HTML,
        // with the plain text portion is split by space.
        jQuery.parseHTML(textFragment).forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const containerElement = document.implementation.createHTMLDocument('New').body
                containerElement.append(node)
                const filteredNode = filterHTML(containerElement)
                if (filteredNode.innerText.length) {
                    newFragments.push(filteredNode.innerHTML)
                }
            } else if (node.nodeType === Node.TEXT_NODE && node.textContent.length) {
                for (const splitTextFragment of node.textContent.split(/(\s)/)) {
                    if (splitTextFragment === '\n' || splitTextFragment.trim().length > 0) {
                        newFragments.push(splitTextFragment)
                    }
                }
            }
        })
    }

    // Remove fragments that are just empty strings.
    return newFragments.filter(Boolean)
}

// Turns a fragment into a gap fragment or a plain fragment.
export function selectFragment(index, fragments) {
    if (fragments[index] instanceof Object) {
        // If fragment at index is a gap, convert it to a plain string using first answer as content.
        fragments[index] = Util.stripTags(fragments[index].answers[0].text)
    } else {

        let sentenceFragmentText = fragments[index]
        let newFragmentIndex = index

        // When clicking on a text fragment where leading or trailing characters make up a sentence boundary,
        // (periods, question marks, exclamation marks, etc.), quotation marks or mid-sentence punctuation
        // (commas, semicolons, em-dash, etc.), automatically move that punctuation to its own fragment
        // before and/or after the current fragment.
        if (sentenceFragmentText.length > 1) {
            let startIndex = 0
            let endIndex = sentenceFragmentText.length
            while (startIndex < endIndex - 1 && hasPunctuation(sentenceFragmentText.charAt(startIndex))) {
                startIndex++
            }
            while (endIndex - 1 > startIndex && hasPunctuation(sentenceFragmentText.charAt(endIndex - 1))) {
                endIndex--
            }
            const leading = sentenceFragmentText.slice(0, startIndex).split('')
            const trailing = sentenceFragmentText.slice(endIndex).split('')
            fragments.splice(index, 1, ...leading, sentenceFragmentText.slice(startIndex, endIndex), ...trailing)
            newFragmentIndex = index + leading.length
            sentenceFragmentText = sentenceFragmentText.slice(startIndex, endIndex).trim()
        }

        let sentenceFragmentAnswers = []
        let newGapOptions = {}

        // If fragment left of the clicked fragment is an existing gap, join them.
        const leftFragment = fragments[newFragmentIndex - 1]
        const hasGapFragmentLeft = leftFragment instanceof Object
        if (hasGapFragmentLeft) {
            newGapOptions = leftFragment
            // Combine the first answer of the left gap with the content of the clicked fragment
            // for the first answer.
            sentenceFragmentText = `${leftFragment.answers[0].text} ${sentenceFragmentText}`
            // Append the existing answers of the left gap.
            sentenceFragmentAnswers = sentenceFragmentAnswers.concat(_.rest(leftFragment.answers))
            // Replace left gap with an empty string to be cleaned up later.
            fragments[newFragmentIndex - 1] = ''
        }

        // If fragment right of the clicked fragment is an existing gap, join them.
        const rightFragment = fragments[newFragmentIndex + 1]
        const hasGapFragmentRight = rightFragment instanceof Object
        if (hasGapFragmentRight) {
            // Merge options from both gaps if needed.
            newGapOptions = {
                ...newGapOptions,
                ...rightFragment,
            }
            // Combine the first answer of the right gap with the content of the clicked fragment
            // for the first answer.
            sentenceFragmentText = `${sentenceFragmentText} ${rightFragment.answers[0].text}`
            // Append the existing answers of the right gap.
            sentenceFragmentAnswers = sentenceFragmentAnswers.concat(_.rest(rightFragment.answers))
            // Replace right gap with an empty string to be cleaned up later.
            fragments[newFragmentIndex + 1] = ''
        }

        // It's common for gap texts to contain many of the same gap types, this makes it such that
        // the new gap has the same type and options as the one before it.
        if (!hasGapFragmentLeft && !hasGapFragmentRight) {
            const previousGap = findPreviousGap(fragments, newFragmentIndex)
            if (previousGap) {
                newGapOptions = {
                    type: previousGap.type,
                    ...pickGapOptions(previousGap),
                }
            }
        }

        // Replace the current fragment with a gap.
        fragments[newFragmentIndex] = {
            id: createNewGapId(fragments),
            type: newGapOptions.type ?? GAP_TYPE.OPEN,
            answers: [{
                text: sentenceFragmentText,
                correct: true,
            }].concat(_.uniq(sentenceFragmentAnswers, false, 'text')),
            ...pickGapOptions(newGapOptions),
        }
    }

    // Remove fragments that are just empty strings.
    return fragments.filter(Boolean)
}

// Join two adjacent fragments together.
export function joinFragment(fragmentIndex, fragments) {
    let nextFragmentIndex = 0
    let nextFragment = fragments.find((fragment, i) => {
        nextFragmentIndex = i
        // Find next fragment that is not just a loose HTML tag or a \n.
        return i > fragmentIndex && Util.stripTags(fragment).trim().length > 0
    })
    if (nextFragment) {
        // If next fragment is a gap.
        if (nextFragment instanceof Object) {
            // Merge answers of the existingFragment and the nextFragment with only
            // uniq text while trying to retain the ordering as much as possible.
            fragments[fragmentIndex].answers = _.uniq(
                fragments[fragmentIndex].answers.concat(_.rest(nextFragment.answers)),
                false,
                'text'
            )
            // Use primary answer with that of the next fragment.
            nextFragment = ` ${nextFragment.answers[0].text}`
        }
        // Remove space between fragments if next fragment is punctuation.
        fragments[fragmentIndex].answers[0].text += `${hasPunctuation(nextFragment) ? '' : ' '}${nextFragment.trim()}`

        // Remove everything until and including that next fragment.
        fragments.splice(fragmentIndex + 1, nextFragmentIndex - fragmentIndex)
    }

    return fragments
}

// Split gap fragment at specific word into two.
export function splitFragment(fragmentIndex, wordIndex, fragments) {
    const existingGapAnswer = fragments[fragmentIndex].answers[0].text

    // Get index of the \s where to split the gap.
    const splitIndex = existingGapAnswer.split(' ', wordIndex + 1).join(' ').length

    // Set the first half of the split to be the new key answer text of the existing gap.
    fragments[fragmentIndex].answers[0].text = existingGapAnswer.substring(0, splitIndex).trim()

    // Create new gap of the same type and options using the second half the split answer.
    const gap = {
        id: createNewGapId(fragments),
        type: fragments[fragmentIndex].type,
        answers: [{
            text: existingGapAnswer.substring(splitIndex).trim(),
            correct: true,
        }],
        ...pickGapOptions(fragments[fragmentIndex]),
    }
    fragments.splice(fragmentIndex + 1, 0, gap)

    return fragments
}

// Generate unique keys for each fragment for reactive Svelte loops.
export function hashFragment(fragment, index) {
    if (fragment instanceof Object) {
        return `${index}___${fragment.id}___${fragment.type}___${fragment.answers[0]?.text}`
    }
    return `${index}___${fragment}`
}

// Check fragments contain at least one gap with at least one correct answer.
export function hasModelAnswer(fragments) {
    return fragments.some((fragment) => {
        if (fragment instanceof Object) {
            return fragment.answers.some((answer) => answer.correct)
        }
        return false
    })
}

// Ensure each gap only contains options valid for its specific type.
export function cleanupGapData(fragments, gradingStrictness) {
    return fragments.map((fragment) => {
        if (fragment instanceof Object) {
            fragment.gradingStrictness = gradingStrictness
            let answers = _.uniq(fragment.answers, false, 'text')
            if (fragment.type === GAP_TYPE.OPEN) {
                answers = answers.reduce((gapAnswers, answer) => {
                    delete answer.index
                    // Remove all distractor answers in open gaps.
                    if (answer.correct) {
                        // Remove HTML from answer, since inputs only support plain text.
                        answer.text = Util.stripTags(answer.text).trim()
                        gapAnswers.push(answer)
                    }
                    return gapAnswers
                }, [])
            } else if (fragment.type === GAP_TYPE.CHOICE || fragment.type === GAP_TYPE.DRAG) {
                // Remove all alternative correct answers, ensure there is only 1 correct answer.
                let foundCorrectAnswer = false
                answers = answers.reduce((gapAnswers, answer) => {
                    delete answer.index
                    // Remove any HTML tags when using gap type choice with dropdown, since
                    // a select input can only render plain text.
                    if (fragment.type === GAP_TYPE.CHOICE && fragment.useDropdownInput === true) {
                        answer.text = Util.stripTags(answer.text)
                    }
                    answer.text = answer.text.trim()
                    if (answer.text) {
                        if (!foundCorrectAnswer && answer.correct === true) {
                            gapAnswers.push(answer)
                            foundCorrectAnswer = true
                        } else if (answer.correct === false) {
                            gapAnswers.push(answer)
                        }
                    }
                    return gapAnswers
                }, [])
            } else if (fragment.type === GAP_TYPE.NUMBER) {
                // Only a single answer is valid for number gaps; discard any alternates leaked from another type.
                answers = answers.slice(0, 1)
            }

            return {
                id: fragment.id,
                type: fragment.type,
                ...pickGapOptions(fragment),
                answers: answers.map((answer) => pickGapAnswerOptions(fragment, answer)),
            }
        }
        return fragment
    })
}

export function applyOptionsToAllGaps(fragments, targetGap, isOnlySameType = false) {
    const targetGapIndex = fragments.indexOf(targetGap)
    return fragments.map((fragment, fragmentIndex) => {
        if (fragment instanceof Object) {
            if (
                (isOnlySameType === false && fragmentIndex > targetGapIndex) ||
                (isOnlySameType === true && fragment.type === targetGap.type && fragment !== targetGap)
            ) {
                if (targetGap.type === GAP_TYPE.NUMBER) {
                    // Skip gaps with content that cannot be parsed as number.
                    if (parseGapInputAsNumber(fragment.answers[0].text).error === null) {
                        return {
                            id: fragment.id,
                            type: GAP_TYPE.NUMBER,
                            ...pickGapOptions(targetGap),
                            answers: [
                                // If the gap is numeric type, copy all answer options except the number text itself.
                                pickGapAnswerOptions(
                                    fragment,
                                    {
                                        ...targetGap.answers[0],
                                        text: fragment.answers[0].text,
                                    }
                                ),
                            ],
                        }
                    }
                    return fragment
                }
                return {
                    id: fragment.id,
                    type: targetGap.type,
                    ...pickGapOptions(targetGap),
                    answers: fragment.answers,
                }
            }
            return fragment
        }
        return fragment
    })
}

export function shouldShowApplyOptionsToAllGapsInput(gapFragments, targetGap, isOnlySameType = false) {
    if (isOnlySameType) {
        return gapFragments.some((otherGap) => otherGap.type === targetGap.type &&
            !hasSameGapOptions(targetGap, otherGap))
    }
    const gapIndex = gapFragments.indexOf(targetGap)
    return gapIndex < gapFragments.length - 1 && gapFragments.some((otherGap, index) => {
        if (index > gapIndex) {
            if (targetGap.type === GAP_TYPE.NUMBER && otherGap.type !== GAP_TYPE.NUMBER) {
                return parseGapInputAsNumber(otherGap.answers[0].text).error === null
            }
            return !hasSameGapOptions(targetGap, otherGap)
        }
        return false
    })
}

// Parse a string as number with the following rules:
// "." or " " as thousands separator
// "," as decimal separator
// trailing ",-" as shorthand for ",00".
export function parseGapInputAsNumber(inputString) {
    let str = inputString.trim()
    if (!str) {
        return { error: 'Empty input' }
    }

    // Support currency shorthand, e.g. "10,-" means "10,00".
    if (str.endsWith(',-')) {
        str = str.slice(0, -2) + ',00'
    }

    // Support negative numbers
    const isNegative = str.startsWith('-')
    if (isNegative) {
        str = str.slice(1)
    }

    // Split the integer and decimal parts
    const parts = str.split(',')
    if (parts.length > 2) {
        return { error: 'Multiple decimal separators' }
    }
    const [integerPart, decimalPart = ''] = parts

    // Remove thousand seperators and check for only digits
    const cleanInteger = integerPart.replace(/[. ]/g, '')
    if (!/^\d+$/.test(cleanInteger)) {
        return { error: 'Invalid integer part' }
    }
    if (decimalPart && !/^\d+$/.test(decimalPart)) {
        return { error: 'Invalid decimal part' }
    }

    const numberStr = (isNegative ? '-' : '') + cleanInteger + (decimalPart ? '.' + decimalPart : '')
    return {
        number: parseFloat(numberStr),
        decimalCount: decimalPart.length,
        error: null
    }
}

export function isNumberAnswerCorrect(answerString, correctAnswer) {
    const { error, number, decimalCount } = parseGapInputAsNumber(answerString)
    if (error) {
        return false
    }

    // A currency must have 0 or 2 decimals
    if (correctAnswer.format.key === 'currency' && ![0, 2].includes(decimalCount)) {
        return false
    }

    // If a fixed number of decimals is required, check it
    if (
        correctAnswer.format.key === 'decimals' &&
        Number.isInteger(correctAnswer.format.value) &&
        correctAnswer.format.value !== decimalCount
    ) {
        return false
    }

    // For an exact answer, the number values must match
    if (correctAnswer.marginType === 'none') {
        return correctAnswer.number === number
    }

    // Absolute margin
    if (correctAnswer.marginType === 'absolute') {
        const margin = Math.abs(correctAnswer.margin)
        const lowerBound = correctAnswer.number - margin
        const upperBound = correctAnswer.number + margin

        return (number >= lowerBound && number <= upperBound)
    }

    // Percentage margin
    if (correctAnswer.marginType === 'percentage') {
        const margin = Math.abs(correctAnswer.number * (correctAnswer.margin / 100))
        const lowerBound = correctAnswer.number - margin
        const upperBound = correctAnswer.number + margin

        return (number >= lowerBound && number <= upperBound)
    }

    return false
}

export function getNumberBound(correctAnswer, isLowerBound) {
    const correctNumber = parseGapInputAsNumber(correctAnswer.text).number
    let margin = 0

    if (correctAnswer.marginType === 'absolute') {
        margin = Math.abs(correctAnswer.margin)
    }

    if (correctAnswer.marginType === 'percentage') {
        margin = Math.abs(correctNumber * (correctAnswer.margin / 100))
    }

    return isLowerBound ? correctNumber - margin : correctNumber + margin
}

function hasPunctuation(s) {
    return /[,.:;!?‽¿¡⸘()…—"“„”‘’«»‹›「『﹃﹁"”’“«»‹›」』﹄﹂]/.test(s)
}

function splitAndTrimString(s, splitPattern) {
    return _.without(_.invoke(s.split(splitPattern), 'trim'), '')
}

function cleanText(inputText) {
    return filterHTML(Util.createHTMLDocumentBody(inputText)).innerHTML.trim()
}

function filterHTML(node) {
    const nodeIterator = document.createNodeIterator(node, NodeFilter.SHOW_ALL)
    let currentNode
    // eslint-disable-next-line no-cond-assign
    while (currentNode = nodeIterator.nextNode()) {
        if (currentNode.nodeType === Node.TEXT_NODE) {
            currentNode.data = currentNode.data.trim()
            continue
        }
        if (currentNode.nodeType === Node.ELEMENT_NODE) {
            // Remove element without any text.
            if (currentNode.innerText.length === 0) {
                currentNode.remove()
                continue
            }
            // Flatten element if if its not included in this list.
            if (!['STRONG', 'EM', 'B', 'I', 'U', 'SUB', 'SUP'].includes(currentNode.nodeName)) {
                currentNode.outerHTML = currentNode.innerHTML
            }
            for (let attributeIndex = currentNode.attributes.length - 1; attributeIndex >= 0; attributeIndex--) {
                currentNode.removeAttribute(currentNode.attributes[attributeIndex].name)
            }
            continue
        }
        // Remove all other node types (comments, attributes, etc.)
        currentNode.remove()
    }
    return node
}

function findPreviousGap(fragments, untilIndex) {
    return fragments[
        _.findLastIndex(fragments, (fragment, existingFragmentIndex) => {
            return existingFragmentIndex < untilIndex && fragment instanceof Object
        })
    ]
}

function createNewGapId(fragments) {
    // New id is highest id + 1
    return Math.max(0, ...fragments.filter(_.isObject).map((fragment) => fragment.id)) + 1
}

function hasSameGapOptions(targetGap, otherGap) {
    if (targetGap.type !== otherGap.type) {
        return false
    }
    if (targetGap.type === GAP_TYPE.NUMBER && otherGap.type === GAP_TYPE.NUMBER) {
        return _.isEqual(
            _.omit(targetGap.answers[0], 'text', 'number'),
            _.omit(otherGap.answers[0], 'text', 'number')
        )
    }
    return _.isEqual(pickGapOptions(targetGap), pickGapOptions(otherGap))
}

function pickGapOptions(gap = {}) {
    switch (gap.type) {
        case GAP_TYPE.OPEN:
            return _.pick(gap, 'isBig', 'gradingStrictness')
        case GAP_TYPE.CHOICE:
            return _.pick(gap, 'useDropdownInput', 'sorting')
        default:
            return {}
    }
}

function pickGapAnswerOptions(gap, answer) {
    const answerOptions = _.pick(answer, 'text', 'correct')
    answerOptions.text = gap.type === GAP_TYPE.OPEN ? Util.stripTags(answerOptions.text) : cleanText(answerOptions.text)
    if (gap.type === GAP_TYPE.NUMBER) {
        answerOptions.marginType = answer.marginType ?? 'none'
        answerOptions.margin = answer.margin ?? 0
        answerOptions.format = answer.format ?? { key: 'decimals', value: null }
        answerOptions.number = parseGapInputAsNumber(answer.text).number ?? null
    }
    return answerOptions
}
